import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shape, arrayOf } from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';
import { getCountries } from 'country-state-picker';
import { includes } from 'lodash';
import {
  Card, Row, Col, Form, Input, Button, Select, DatePicker, Upload, notification, Avatar, Divider,
} from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import { readAsDataUrl, uploadFile } from '../../../../upload';
import { SETUP_USER_HOME } from '../../../../routes';
import { REGISTER_STUDENT } from '../../../../graphql';
import { makeStateOption, countriesOptions } from '../../common/formUtils';
import CohortField from '../../common/CohortField';

const { Item: FormItem, useForm } = Form;
const { Option } = Select;

const StudentForm = ({ cohorts }) => {
  const [availableCohort, setAvailableCohort] = useState([]);
  const [states, setStates] = useState(makeStateOption('ng'));
  const [phoneCode, setPhoneCode] = useState('+234');
  const client = useApolloClient();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [profilePicture, setProfilePicture] = useState({ file: null, imgBase64: '' });
  const [registerStudent,
    { data: response, loading: regLoading },
  ] = useMutation(REGISTER_STUDENT);
  const [form] = useForm();
  const navigate = useNavigate();
  useEffect(() => {
    if (cohorts.length > 0) {
      setAvailableCohort(cohorts);
    }
  }, [cohorts]);

  /**
   * Enrolment is a list of cohorts and the subjects taken in each. "All" means
   * every subject of that cohort, resolved here from the cohort the picker
   * already loaded.
   */
  const collectEnrolments = (userCohort = []) => {
    const cohortIds = [];
    let subjectIds = [];

    userCohort.filter(Boolean).forEach(({ cohort, subject }) => {
      if (!cohort) return;
      cohortIds.push(cohort);
      if (!subject) return;

      if (includes(subject, 'ALL')) {
        const chosen = availableCohort.find(({ id }) => id === cohort);
        subjectIds = [...subjectIds, ...(chosen?.subjects ?? []).map(({ id }) => id)];
      } else {
        subjectIds = [...subjectIds, ...subject];
      }
    });

    return { cohortIds, subjectIds: [...new Set(subjectIds)] };
  };

  const handleFormSubmit = async (inputs) => {
    const {
      firstName, lastName, email, userName, parentName, gender, address, dob,
      parentPhoneNumber, state, zipCode, country,
    } = inputs;

    const { cohortIds, subjectIds } = collectEnrolments(inputs.userCohort);

    // No password: the server generates one and emails it with the account
    // details, so it is never typed in by an administrator or sent over the
    // wire from here.
    const newUser = {
      firstName,
      lastName,
      email,
      userName,
      accountType: 'STUDENT',
      cohortIds,
      subjectIds,
      profileStudent: {
        parentName,
        gender,
        address,
        dob: dob ? dob.toISOString() : undefined,
        parentPhoneNumber: `${phoneCode}${parentPhoneNumber}`,
        state,
        country,
        zipCode,
      },
    };

    try {
      const { data } = await registerStudent({ variables: { input: newUser } });

      // Uploaded after the account exists, so the photograph attaches to it
      // rather than being stranded if registration fails.
      if (profilePicture.file) {
        setUploadLoading(true);
        try {
          await uploadFile({
            client,
            file: profilePicture.file,
            target: 'PROFILE_IMAGE',
            targetId: data.registerUser.id,
          });
        } finally {
          setUploadLoading(false);
        }
      }
    } catch (error) {
      notification.error({
        message: error.message || 'Error Occurred',
        description: 'Error registering student',
      });
    }
  };

  useEffect(() => {
    if (response && response.registerUser) {
      notification.success({ message: 'New Student added', description: 'Student registration successful' });
      navigate(SETUP_USER_HOME);
    }
  }, [response, navigate]);

  const handleFileChange = ({ file }) => {
    readAsDataUrl(file).then((imgBase64) => setProfilePicture({ file, imgBase64 }));
  };
  const handleForm = ((field) => {
    if (field.userCohort) {
      const selectedCohort = form.getFieldValue('userCohort');
      if (selectedCohort && selectedCohort.length > 0) {
        const validItems = selectedCohort.map((item) => {
          if (item) return item.cohort;
          return undefined;
        });
        const remainsCohort = cohorts.map((item) => {
          if (includes(validItems, item.id)) return { ...item, selected: true };
          return { ...item, selected: false };
        });
        setAvailableCohort(remainsCohort);
      }
    }
    if (field.country) {
      const countryInfo = getCountries().filter(({ name }) => name === field.country)[0];
      const newStates = makeStateOption(countryInfo.code);
      setPhoneCode(countryInfo.dial_code);
      form.resetFields(['state']);
      setStates(newStates);
    }
  });

  return (
    <div className="profile-teacher-full">
      <Row>
        <Col md={16} xs={24}>
          <Card className="profile-card gx-pr-0 gx-pl-0" title="Student Registration">
            <Form scrollToFirstError onValuesChange={handleForm} form={form} onFinish={handleFormSubmit} name="Student_Form" layout="vertical">
              <Row style={{ flexDirection: 'row' }} justify="space-between" className="no-flex-column form-row">
                <Col sm={24} md={12}>
                  <FormItem name="firstName" label="First Name" rules={[{ required: true, type: 'string', message: 'provide first name' }]}>
                    <Input placeholder="Enter first name" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Last Name" name="lastName" rules={[{ required: true, type: 'string', message: 'provide last name' }]}>
                    <Input placeholder="Enter last name" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Student Email" name="email" rules={[{ required: true, type: 'email', message: 'provide student email' }]}>
                    <Input placeholder="Enter student email" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Gender" name="gender" rules={[{ required: true, message: 'provide gender' }]}>
                    <Select placeholder="select gender">
                      <Option value="MALE">Male</Option>
                      <Option value="FEMALE">Female</Option>
                    </Select>
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Username" name="userName" rules={[{ required: true, type: 'string', message: 'provide username' }]}>
                    <Input placeholder="Enter Username" />
                  </FormItem>
                </Col>
                <Col sm={24}>
                  <FormItem label="Address" name="address" rules={[{ required: true, type: 'string', message: 'provide student address' }]}>
                    <Input placeholder="Student Address" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Date of Birth" name="dob" rules={[{ required: true, message: 'provide student email' }]}>
                    <DatePicker placeholder="select date" className="gx-w-100" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Zip Code" name="zipCode" rules={[{ required: false, type: 'string', message: 'provide student zip code' }]}>
                    <Input placeholder="Zip Code" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem initialValue="Nigeria" label="Country" name="country" rules={[{ required: true, type: 'string', message: 'provide student country' }]}>
                    <Select
                      filterOption={(input, option) => {
                        const countryName = option.label.toLowerCase();
                        return countryName.includes(input.toLocaleLowerCase());
                      }}
                      showSearch
                      placeholder="Country"
                      options={countriesOptions}
                      autoComplete="new-password"
                    />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="State of origin" name="state" rules={[{ required: true }]}>
                    <Select
                      filterOption={(input, option) => {
                        const stateName = option.label.toLowerCase();
                        return stateName.includes(input.toLocaleLowerCase());
                      }}
                      showSearch
                      placeholder="State of origin"
                      options={states}
                      autoComplete="new-password"
                    />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem label="Parent Name" name="parentName" rules={[{ required: true, type: 'string', message: 'provide parent name' }]}>
                    <Input autoComplete="off" placeholder="Child's parent name" />
                  </FormItem>
                </Col>
                <Col sm={24} md={12}>
                  <FormItem
                    label="Parent Phone Number"
                    rules={[{
                      required: false, type: 'string', min: 10, max: 12, message: 'provide valid phone number',
                    }]}
                    name="parentPhoneNumber"
                  >
                    <Input addonBefore={phoneCode} type="number" placeholder="Enter parent phone number" />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <Divider>Assign Cohort</Divider>
                </Col>
                <CohortField availableCohorts={availableCohort} />
              </Row>
              <Row className="no-flex-column form-row gx-mt-1">
                <Col sm={24}>
                  <Button className="gx-w-48"><Link to={SETUP_USER_HOME}>Cancel</Link></Button>
                  <Button loading={regLoading || uploadLoading} className="gx-w-48" htmlType="submit" type="primary">Submit</Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
        <Col md={8} xs={24}>
          <Card className="profile-card picture" title="Profile Picture">
            <div className="photo-card">
              <div className="avatar-wrapper">
                <Avatar style={{ backgroundColor: '#00CC96' }} icon={<UserOutlined size="large" />} src={profilePicture.imgBase64} size="large" className="teacher-avatar" />
              </div>
              <Upload
                customRequest={handleFileChange}
                accept="image/png,image/jpeg,image/jpg"
                showUploadList={false}
              >
                <Button
                  className="upload-btn"
                  icon={<UploadOutlined />}
                >
                  Add Picture
                </Button>
              </Upload>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

StudentForm.propTypes = {
  cohorts: arrayOf(shape({})).isRequired,
};

export default StudentForm;
